/**
 * Real-time notifications hook for internal partners
 * Listens for new messages and meeting reminders
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { db } from '@/lib/firebase';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  onSnapshot, 
  Timestamp,
  limit,
  addDoc,
  serverTimestamp
} from 'firebase/firestore';
import { toast } from 'sonner';

interface PartnerNotification {
  id: string;
  type: 'message' | 'meeting_reminder' | 'new_client' | 'mandate_update';
  title: string;
  message: string;
  createdAt: Date;
  seen: boolean;
  actionUrl?: string;
  metadata?: Record<string, any>;
}

interface Meeting {
  id: string;
  title: string;
  scheduledAt: Timestamp;
  attendees: string[];
  meetingLink: string;
}

interface Message {
  id: string;
  senderName: string;
  content: string;
  createdAt: Timestamp;
}

export function usePartnerNotifications(partnerType?: string) {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<PartnerNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Request browser notification permission
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Send browser notification
  const sendBrowserNotification = useCallback((title: string, body: string, url?: string) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      const notification = new Notification(title, {
        body,
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        tag: `partner-${Date.now()}`,
      });

      if (url) {
        notification.onclick = () => {
          window.focus();
          window.location.href = url;
        };
      }
    }
  }, []);

  // Check for meeting reminders
  const checkMeetingReminders = useCallback((meetings: Meeting[]) => {
    const now = new Date();
    
    meetings.forEach(meeting => {
      const meetingTime = meeting.scheduledAt.toDate();
      const timeDiff = meetingTime.getTime() - now.getTime();
      const minutesUntil = Math.floor(timeDiff / 60000);

      // Reminder at 15 minutes
      if (minutesUntil > 0 && minutesUntil <= 15) {
        const existingReminder = notifications.find(
          n => n.type === 'meeting_reminder' && n.metadata?.meetingId === meeting.id
        );

        if (!existingReminder) {
          const newNotification: PartnerNotification = {
            id: `reminder-${meeting.id}-${Date.now()}`,
            type: 'meeting_reminder',
            title: '⏰ Meeting Starting Soon',
            message: `"${meeting.title}" starts in ${minutesUntil} minutes`,
            createdAt: new Date(),
            seen: false,
            actionUrl: meeting.meetingLink,
            metadata: { meetingId: meeting.id },
          };

          setNotifications(prev => [newNotification, ...prev]);
          setUnreadCount(prev => prev + 1);
          
          toast.info(`Meeting "${meeting.title}" starts in ${minutesUntil} minutes`, {
            action: {
              label: 'Join',
              onClick: () => window.open(meeting.meetingLink, '_blank'),
            },
          });

          sendBrowserNotification(
            '⏰ Meeting Starting Soon',
            `"${meeting.title}" starts in ${minutesUntil} minutes`,
            meeting.meetingLink
          );
        }
      }
    });
  }, [notifications, sendBrowserNotification]);

  // Listen for meetings
  useEffect(() => {
    if (!user?.email) return;

    const meetingsQuery = query(
      collection(db, 'partnerMeetings'),
      where('partnerId', '==', user.email),
      where('status', '==', 'scheduled'),
      orderBy('scheduledAt', 'asc'),
      limit(20)
    );

    const unsubscribe = onSnapshot(meetingsQuery, (snapshot) => {
      const meetings = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as Meeting[];

      // Check for reminders every minute
      const interval = setInterval(() => {
        checkMeetingReminders(meetings);
      }, 60000);

      // Initial check
      checkMeetingReminders(meetings);

      return () => clearInterval(interval);
    });

    return () => unsubscribe();
  }, [user?.email, checkMeetingReminders]);

  // Listen for new messages
  useEffect(() => {
    if (!user?.email) return;

    const messagesQuery = query(
      collection(db, 'partnerConversations'),
      where('partnerId', '==', user.email),
      where('unread', '==', true),
      orderBy('lastMessageAt', 'desc'),
      limit(10)
    );

    const unsubscribe = onSnapshot(messagesQuery, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === 'added') {
          const data = change.doc.data();
          const notification: PartnerNotification = {
            id: `msg-${change.doc.id}`,
            type: 'message',
            title: `💬 New message from ${data.senderName || 'Client'}`,
            message: data.lastMessage?.substring(0, 100) || 'New message received',
            createdAt: data.lastMessageAt?.toDate() || new Date(),
            seen: false,
            actionUrl: `/internal-partners/${partnerType}/messages`,
            metadata: { conversationId: change.doc.id },
          };

          setNotifications(prev => {
            // Avoid duplicates
            if (prev.some(n => n.id === notification.id)) return prev;
            return [notification, ...prev].slice(0, 50);
          });

          setUnreadCount(prev => prev + 1);

          toast.info(`New message from ${data.senderName || 'Client'}`, {
            description: data.lastMessage?.substring(0, 50),
          });

          sendBrowserNotification(
            `New message from ${data.senderName || 'Client'}`,
            data.lastMessage?.substring(0, 100) || 'New message received'
          );
        }
      });
    });

    return () => unsubscribe();
  }, [user?.email, partnerType, sendBrowserNotification]);

  // Load initial notifications
  useEffect(() => {
    if (!user?.email) return;

    const notificationsQuery = query(
      collection(db, 'partnerNotifications'),
      where('partnerId', '==', user.email),
      orderBy('createdAt', 'desc'),
      limit(50)
    );

    const unsubscribe = onSnapshot(notificationsQuery, (snapshot) => {
      const notificationsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
      })) as PartnerNotification[];

      setNotifications(notificationsList);
      setUnreadCount(notificationsList.filter(n => !n.seen).length);
      setLoading(false);
    }, () => {
      // Firestore might fail if collection doesn't exist
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user?.email]);

  const markAsRead = useCallback((notificationId: string) => {
    setNotifications(prev => 
      prev.map(n => n.id === notificationId ? { ...n, seen: true } : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, seen: true })));
    setUnreadCount(0);
  }, []);

  const addNotification = useCallback(async (notification: Omit<PartnerNotification, 'id' | 'createdAt' | 'seen'>) => {
    if (!user?.email) return;

    try {
      await addDoc(collection(db, 'partnerNotifications'), {
        ...notification,
        partnerId: user.email,
        createdAt: serverTimestamp(),
        seen: false,
      });
    } catch (error) {
      console.error('Error adding notification:', error);
    }
  }, [user?.email]);

  return {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    addNotification,
  };
}